import React, { useState, useEffect, useRef } from 'react';
import { Tag, Check, X } from 'lucide-react';

interface QuickTagInputProps {
  onTagAdd: (tagName: string) => void;
  onCancel: () => void;
  suggestedTags?: string[];
  autoFocus?: boolean;
}

export const QuickTagInput: React.FC<QuickTagInputProps> = ({
  onTagAdd,
  onCancel,
  suggestedTags = ['USPS', 'FedEx', 'UPS', 'Amazon'],
  autoFocus = true
}) => {
  const [tagName, setTagName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = () => {
    const trimmed = tagName.trim();
    if (trimmed) {
      onTagAdd(trimmed);
      setTagName('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const filteredSuggestions = suggestedTags.filter(tag =>
    tag.toLowerCase().includes(tagName.toLowerCase())
  );

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px',
        background: '#fbf9f4',
        border: '2px solid #23211d',
        borderRadius: '6px',
        boxShadow: '0 0 0 3px rgba(35, 33, 29, 0.1)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Tag size={12} style={{ color: '#23211d', marginLeft: '4px' }} />
      <input
        ref={inputRef}
        type="text"
        value={tagName}
        onChange={(e) => {
          setTagName(e.target.value);
          setShowSuggestions(e.target.value.length > 0);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowSuggestions(tagName.length > 0)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder="Type tag..."
        style={{
          border: 'none',
          outline: 'none',
          padding: '4px 6px',
          fontSize: '0.75rem',
          width: '100px',
          fontWeight: '600'
        }}
      />

      {/* Quick suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: '#fbf9f4',
            border: '1px solid #e2ddd1',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: "var(--z-modal)",
            minWidth: '120px'
          }}
        >
          {filteredSuggestions.slice(0, 4).map(tag => (
            <button
              key={tag}
              onMouseDown={(e) => {
                e.preventDefault();
                setTagName(tag);
                handleSubmit();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#4a463f'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#efebe1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!tagName.trim()}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          border: 'none',
          background: tagName.trim() ? '#5d7148' : '#e2ddd1',
          borderRadius: '4px',
          cursor: tagName.trim() ? 'pointer' : 'not-allowed',
          transition: 'background 0.2s'
        }}
        title="Add tag (Enter)"
      >
        <Check size={12} style={{ color: tagName.trim() ? 'white' : '#a39d90' }} />
      </button>

      <button
        onClick={onCancel}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#857f73'
        }}
        title="Cancel (Esc)"
      >
        <X size={12} />
      </button>
    </div>
  );
};
